import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Clone } from '../../../shared/utils/clone';
import { RemoveProperty } from '../../../shared/utils/remove-property/remove-property';
import { API } from '../../constants/api';
import { CONSTANTS } from '../../constants/constants';
import { Mesh } from '../../interfaces/mesh/mesh';
import {
  Catenary,
  Catenary3D,
  Catenary3DAnswer,
  CatenaryAnswer,
  ValidationError,
} from '../../models/catenary-answer';
import { Connection } from '../../models/connection.model';
import { Simulation } from '../../models/simulation.model';
import { DataSharingService } from '../data-sharing/data-sharing.service';
import { ConnectionService } from '../simulation/objects/connection/connection.service';
import { ReferenceSystemService } from '../simulation/objects/reference-system/reference-system.service';
import { SimulationManagerService } from '../simulation/simulation-manager.service';

@Injectable({
  providedIn: 'root',
})
export class CatenaryService {
  public catenaries3D$ = new BehaviorSubject<Catenary3D[]>(null);
  public viewProperties = new BehaviorSubject<any>(null);
  public catenary$ = new BehaviorSubject<CatenaryAnswer>(null);
  public mesh$ = new BehaviorSubject<Mesh[]>(null);

  constructor(
    private httpClient: HttpClient,
    private simulationManager: SimulationManagerService,
    private connectionService: ConnectionService,
    private referenceSystemService: ReferenceSystemService,
    private dataSharingService: DataSharingService,
  ) {}

  clearCatenary(): void {
    this.catenary$.next(null);
  }

  enrichSimulation(simulation: Simulation): Simulation {
    const enrichedSimulation = Clone(simulation);
    this.connectionService.assignDefaultConnection(enrichedSimulation);
    RemoveProperty(enrichedSimulation, new Set([CONSTANTS.GRAPHIC_PROPERTIES]));
    return enrichedSimulation;
  }

  getCatenary(): Observable<CatenaryAnswer> {
    const simulation = this.simulationManager.getSimulation();
    const enrichedSimulation = this.enrichSimulation(simulation);

    return this.httpClient.post<CatenaryAnswer>(API.CATENARY_GENERATION, enrichedSimulation).pipe(
      tap((response) => {
        this.updateViewProperties(response.lines);
        this.catenary$.next(response);
        this.dataSharingService.updateErrorsLog$.next(this.getErrors(response));
      }),
      catchError((err) => {
        this.catenary$.next(null);
        this.dataSharingService.updateErrorsLog$.next(this.getErrors(err.error));
        return throwError(err);
      }),
    );
  }

  getCatenary3D(simulation: Simulation): Observable<Catenary3DAnswer> {
    const enrichedSimulation = this.enrichSimulation(simulation);

    return this.httpClient.post<Catenary3DAnswer>(API.CATENARY_3D, enrichedSimulation).pipe(
      tap(async (response) => {
        this.updateViewProperties(response.lines);
        this.catenaries3D$.next(response?.lines);
        await this.getFiniteElements(enrichedSimulation);
        return this.dataSharingService.updateErrorsLog$.next();
      }),
      catchError((err) => {
        this.dataSharingService.updateErrorsLog$.next();
        return throwError(err);
      }),
    );
  }

  async getFiniteElements(simulation: Simulation): Promise<Mesh[]> {
    return this.httpClient
      .post<Mesh[]>(API.FINITE_ELEMENTS, simulation)
      .pipe(
        tap((mesh) => {
          this.emitReferenceSystemChanges(mesh);
          this.mesh$.next(mesh);
        }),
        catchError((error) => throwError(error)),
      )
      .toPromise();
  }

  emitReferenceSystemChanges(mesh: Mesh[]): void {
    const oldMesh = this.mesh$.getValue();
    if (!oldMesh) {
      return;
    }

    const changed = mesh.some((meshByLine, index) => {
      const currentNodesStr = JSON.stringify(meshByLine.webNodes);
      const oldNodesStr = JSON.stringify(oldMesh[index]?.webNodes);
      return currentNodesStr !== oldNodesStr;
    });

    if (!changed) {
      return;
    }

    this.referenceSystemService.resetAllByMesh(mesh, (connection: Connection) =>
      this.connectionService.getAsGlobal(connection),
    );
  }

  updateViewProperties(lines: Catenary[]): void {
    if (!lines || !lines?.length) {
      this.viewProperties.next(null);
      return;
    }
    const data = lines.map((l) => {
      const {
        lineTitle,
        projection,
        angle,
        tension,
        horizontalTension,
        verticalTension,
        inputProjection,
        inputAngle,
        inputTension,
        inputHorizontalTension,
        inputVerticalTension,
      } = l;
      return {
        lineTitle,
        projection,
        angle,
        tension,
        horizontalTension,
        verticalTension,
        inputProjection,
        inputAngle,
        inputTension,
        inputHorizontalTension,
        inputVerticalTension,
      };
    });

    this.viewProperties.next(data);
  }

  getErrors(response): ValidationError[] {
    return response?.validationErrors ?? null;
  }
}
